// Portfolio Administration v1.0 PR 6 — TanStack Query hooks for the
// Waterfalls config editor and the cross-portfolio Loan Tape page.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  loanTapePeriodApi,
  waterfallConfigsApi,
} from "@/api/portfolioAdmin/pr6Client";
import type {
  ApproveWaterfallRequest,
  CreateWaterfallDraftRequest,
  LoanTapePeriodViewQuery,
  RejectWaterfallRequest,
  UpdateWaterfallDraftRequest,
  WaterfallPriorityExplainRequest,
} from "@/api/portfolioAdmin/pr6Types";

// ----- query keys -------------------------------------------------------

export const pr6Keys = {
  summary: ["portfolio-admin", "waterfalls", "summary"] as const,
  active: (code: string) =>
    ["portfolio-admin", "waterfalls", code, "active"] as const,
  history: (code: string) =>
    ["portfolio-admin", "waterfalls", code, "history"] as const,
  runs: (code: string) =>
    ["portfolio-admin", "waterfalls", code, "runs"] as const,
  config: (configId: string) =>
    ["portfolio-admin", "waterfalls", "config", configId] as const,
  priorityDetail: (configId: string, position: number) =>
    [
      "portfolio-admin",
      "waterfalls",
      "config",
      configId,
      "priority",
      position,
    ] as const,
  loanTapePeriod: (q: LoanTapePeriodViewQuery) =>
    [
      "portfolio-admin",
      "loan-tape",
      "period",
      q.period,
      q.statuses ?? null,
      q.variance ?? null,
      q.minDaysLate ?? null,
      q.search ?? null,
    ] as const,
};

// ----- waterfall config -------------------------------------------------

export function useWaterfallsSummary(runHistoryDays?: number) {
  return useQuery({
    queryKey: [...pr6Keys.summary, runHistoryDays ?? 30] as const,
    queryFn: () => waterfallConfigsApi.summary(runHistoryDays),
  });
}

export function useWaterfallHistory(code: string | undefined) {
  return useQuery({
    queryKey: pr6Keys.history(code ?? ""),
    queryFn: () => waterfallConfigsApi.history(code!),
    enabled: !!code,
  });
}

export function useWaterfallRunsForCode(code: string | undefined, limit?: number) {
  return useQuery({
    queryKey: [...pr6Keys.runs(code ?? ""), limit ?? 30] as const,
    queryFn: () => waterfallConfigsApi.runs(code!, limit),
    enabled: !!code,
  });
}

export function useWaterfallConfig(configId: string | undefined) {
  return useQuery({
    queryKey: pr6Keys.config(configId ?? ""),
    queryFn: () => waterfallConfigsApi.config(configId!),
    enabled: !!configId,
  });
}

/** Drill-down for one priority row. Enabled-gated so the fetch only
 *  fires when the detail drawer actually opens. */
export function useWaterfallPriorityDetail(
  configId: string | undefined,
  position: number | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: pr6Keys.priorityDetail(configId ?? "", position ?? -1),
    queryFn: () => waterfallConfigsApi.priorityDetail(configId!, position!),
    enabled: enabled && !!configId && position !== undefined,
  });
}

/** "Explain this line" AI coach — a POST, but purely a read. */
export function useExplainWaterfallPriority() {
  return useMutation({
    // no-invalidation: read-only AI explanation; no cached reads change
    mutationFn: (args: {
      configId: string;
      position: number;
      body: WaterfallPriorityExplainRequest;
    }) =>
      waterfallConfigsApi.explainPriority(
        args.configId,
        args.position,
        args.body,
      ),
  });
}

export function useCreateWaterfallDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWaterfallDraftRequest) =>
      waterfallConfigsApi.createDraft(code, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr6Keys.summary });
      qc.invalidateQueries({ queryKey: pr6Keys.history(code) });
    },
  });
}

export function useUpdateWaterfallDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { configId: string; body: UpdateWaterfallDraftRequest }) =>
      waterfallConfigsApi.updateDraft(args.configId, args.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pr6Keys.summary });
      qc.invalidateQueries({ queryKey: pr6Keys.history(code) });
      qc.invalidateQueries({ queryKey: pr6Keys.config(data.id) });
    },
  });
}

export function useProposeWaterfallDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (configId: string) => waterfallConfigsApi.propose(configId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pr6Keys.summary });
      qc.invalidateQueries({ queryKey: pr6Keys.history(code) });
      qc.invalidateQueries({ queryKey: pr6Keys.config(data.id) });
    },
  });
}

export function useApproveWaterfallDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { configId: string; body: ApproveWaterfallRequest }) =>
      waterfallConfigsApi.approve(args.configId, args.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pr6Keys.summary });
      qc.invalidateQueries({ queryKey: pr6Keys.history(code) });
      qc.invalidateQueries({ queryKey: pr6Keys.config(data.id) });
    },
  });
}

export function useRejectWaterfallDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { configId: string; body: RejectWaterfallRequest }) =>
      waterfallConfigsApi.reject(args.configId, args.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: pr6Keys.summary });
      qc.invalidateQueries({ queryKey: pr6Keys.history(code) });
      qc.invalidateQueries({ queryKey: pr6Keys.config(data.id) });
    },
  });
}

// ----- cross-portfolio loan tape ----------------------------------------

export function useLoanTapePeriodView(q: LoanTapePeriodViewQuery) {
  return useQuery({
    queryKey: pr6Keys.loanTapePeriod(q),
    queryFn: () => loanTapePeriodApi.listForPeriod(q),
    enabled: !!q.period,
  });
}

// SP-6 — useLockSelectedLoanTapeEntries (+ loanTapePeriodApi.lockSelected) was
// removed: the legacy tape is read-only, so there is no "lock selected" action.
// The monthly-close page keeps its read + PSA-export surface.
